import React, { useEffect, useState } from 'react';
import PropTypes, { any } from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment-timezone';

import {
    ToggleSwitch,
    InputGroupPre,
    TitleSwitch,
    DateTimePicker,
    Textarea,
    CheckBox,
} from '../../../Form';
import { isDinglesOrDoublesFormat, numRegExp } from '../../../../utils/events';
import { teamPaymentTypeIds, defaultMinimumEntryFee } from '../../../../utils/eventConstants';
import { required } from '../../../../utils/form-validation';
import { showInfoPopup } from '../../../../actions/notification-actions';

function EntryFee(props) {
    const {
        field,
        value,
        formApi,
        eventEntryFeeField,
        currency,
        minimumEntryFee,
        formattedMinimumEntryFee,
        playersRegistered,
    } = props;

    const { values } = formApi;

    const [showWithdrawFee, setShowWithdrawFee] = useState(values.registrationCancellationPolicy?.isActive || false);
    const [showWithdrawFeeDate, setShowWithdrawFeeDate] = useState(!!values.registrationCancellationPolicy?.effectiveAfterDateTimeUtc);
    const [effectiveDate, setEffectiveDate] = useState();
    const [tourDateEnd, setTourDateEnd] = useState();
    const [regDateStart, setRegDateStart] = useState();
    const dispatch = useDispatch();

    const coreUI = useSelector(({ coreUI }) => coreUI);

    useEffect(() => {
        if (values?.eventSchedule) {
            const { eventSchedule, registrationCancellationPolicy } = values;
            const timeZone = eventSchedule.timeZone || 77;
            const timeZoneObj = coreUI.types.timeZones.find(tz => { return tz.id === parseInt(timeZone); });

            setRegDateStart(moment.utc(eventSchedule.registrationStartUtc).tz(timeZoneObj.value));
            setTourDateEnd(moment.utc(eventSchedule.eventEndUtc).tz(timeZoneObj.value));

            if (registrationCancellationPolicy?.effectiveAfterDateTimeUtc) {
                setEffectiveDate(moment.utc(registrationCancellationPolicy.effectiveAfterDateTimeUtc).tz(timeZoneObj.value));
            }
        }
    }, [values, coreUI.types.timeZones]);

    const minimumFee = minimumEntryFee ?? defaultMinimumEntryFee;
    const validateEventEntryFee = (val, currencySymbol, minimumFee) => {
        const minimumFeeCheck = val && !!minimumFee && numRegExp.test(val) && val >= minimumFee;
        if (!val || !minimumFeeCheck) {
            return {
                error: `Fee must be at least ${formattedMinimumEntryFee}.`,
            };
        }
    };

    const validateWithdrawalFee = (val, eventEntryFee) => {
        const withdrawalFeeCheck = !val || parseInt(val) === 0 || (val && !!eventEntryFee && numRegExp.test(val) && parseInt(val) < parseInt(eventEntryFee) && parseInt(val) >= 1);
        if (!withdrawalFeeCheck) {
            return {
                error: `Withdrawal fee must be less than the player entry fee.`,
            };
        }
    };

    const handleEventEntryFee = (val, eventEntryFeeField, formApi) => {
        if (!val) {
            formApi.setValue(eventEntryFeeField, null);
            formApi.setError(eventEntryFeeField, null);
            const registrationCancellationPolicy = formApi.getValue('registrationCancellationPolicy');

            if (registrationCancellationPolicy?.withdrawalFee && !playersRegistered) {
                formApi.setValue('registrationCancellationPolicy.withdrawalFee', null);
                formApi.reset('registrationCancellationPolicy.withdrawalFee');
                formApi.setError('registrationCancellationPolicy', null);
            }
        }
        const eventDivisions = formApi.values.eventDivisions || [];
        if (val && eventDivisions.length > 0) {
            eventDivisions.forEach((div, i) => {
                if (isDinglesOrDoublesFormat(parseInt(div?.divisionFormatType))) {
                    formApi.setValue(['eventDivisions', i, 'teamPaymentTypeId'], teamPaymentTypeIds.PAY_SEPARATELY);
                }
            });
        }
    };

    useEffect(() => {
        if (value) {
            handleEventEntryFee(value, eventEntryFeeField, formApi);
        }
    }, [value]);

    const toggleWithdrawFee = () => {
        formApi.setValue('registrationCancellationPolicy.isActive', !showWithdrawFee);
        setShowWithdrawFee(!showWithdrawFee);

        if (showWithdrawFee && !playersRegistered) {
            setEffectiveDate();
            setShowWithdrawFeeDate(false);
            formApi.setValue('registrationCancellationPolicy.description', null);
            formApi.reset('registrationCancellationPolicy.description');
            formApi.setValue('registrationCancellationPolicy.withdrawalFee', null);
            formApi.reset('registrationCancellationPolicy.withdrawalFee');
            formApi.setValue('registrationCancellationPolicy.effectiveAfterDateTimeUtc', null);
            formApi.reset('registrationCancellationPolicy.effectiveAfterDateTimeUtc');
            formApi.setError('registrationCancellationPolicy', null);
        }
    };

    const toggleWithdrawFeeDate = () => {
        setShowWithdrawFeeDate(!showWithdrawFeeDate);
        if (showWithdrawFeeDate) {
            setEffectiveDate();
            formApi.setValue('registrationCancellationPolicy.effectiveAfterDateTimeUtc', null);
            formApi.reset('registrationCancellationPolicy.effectiveAfterDateTimeUtc');
            formApi.setError('registrationCancellationPolicy', null);
        }
    };

    const handleEffectiveDateChange = (date) => {
        setEffectiveDate(date);
        formApi.setValue('registrationCancellationPolicy.effectiveAfterDateTimeUtc', date.toISOString());
    };

    const onToggleChange = (val) => {
        if (val) {
            dispatch(showInfoPopup({
                title: 'Info on Entry Fees',
                message: (
                    <>
                        <li>Entry fees are optional, and if enabled, players will be required to pay the set entry fee in addition to any division fees for which they register.</li>
                        <li>If entry fee is enabled, any doubles divisions will be required to use the ‘pay separately’ payment option.</li>
                        <li>Entry fee minimum amount is {formattedMinimumEntryFee}.</li>
                        <li>A Stripe processing fee will be assessed on each entry fee. Stripe fees will vary by country and account. Contact Stripe if you have any questions.</li>
                    </>
                ),
                confirmButtonLabel: 'Enable',
                onConfirmClick: () => {
                    formApi.setValue('hasEntryFee', true);
                    handleEventEntryFee(val, eventEntryFeeField, formApi);
                },
                showCancelButton: true,
                onCancelClick: () => {
                    formApi.setValue('hasEntryFee', false);
                    handleEventEntryFee(false, eventEntryFeeField, formApi);
                },
            }));
        } else {
            formApi.setValue('hasEntryFee', false);
            handleEventEntryFee(val, eventEntryFeeField, formApi);
        }
    };

    return (
        <div className={`col-12`}>
            <div className="div div16" />
            <ToggleSwitch
                id={'hasEntryFee'}
                field={field}
                formGroupCssClass={`mt16`}
                label="Add a player entry fee in addition to division fees?"
                onChange={val => onToggleChange(val)}
                data-testid="event-edit.entry-fee"
                hoverText={(
                    <>
                        <li>Entry fees are optional, and if enabled, players will be required to pay the set entry fee in addition to any division fees for which they register.</li>
                        <li>If entry fee is enabled, any doubles divisions will be required to use the ‘pay separately’ payment option.</li>
                        <li>Entry fee minimum amount is {formattedMinimumEntryFee}.</li>
                        <li>A Stripe processing fee will be assessed on each entry fee. Stripe fees will vary by country and account. Contact Stripe if you have any questions.</li>
                    </>
                )}
                reverse={false}
                disabled={playersRegistered}
                dontSetValueOnToggle={true}
            />
            {value && (
                <div className="bg-white p16">
                    <InputGroupPre
                        id={`entryFee`}
                        label="Player entry fee"
                        hiddenLabel={true}
                        field={eventEntryFeeField}
                        type="number"
                        pre={currency.symbol}
                        placeholder="E.g. 45"
                        validate={val => validateEventEntryFee(val, currency.symbol, minimumFee)}
                        className="hide-spinner"
                        format="decimal"
                        themeV3
                        data-testid="event-edit.entry-fee-input"
                    />
                </div>
            )}
            <TitleSwitch
                id="withdrawFeeActive"
                data-testid="event-edit.withdraw-fee.toggle"
                formGroupCssClass={`mt16`}
                label={'Add a registration withdrawal policy / fee'}
                value={showWithdrawFee}
                onChange={toggleWithdrawFee}
            />
            {showWithdrawFee && (
                <div className="bg-white p16">
                    <Textarea
                        id="description"
                        field="registrationCancellationPolicy.description"
                        rows="5"
                        themeV3
                        label="Withdraw policy"
                        placeholder="Add a withdraw policy"
                            data-testid="event-edit.withdrawal-fee-textarea"
                    />
                    <div className="mt16">
                        <InputGroupPre
                            id={`withdrawalFee`}
                            label="Withdrawal fee"
                            hiddenLabel={true}
                            field={'registrationCancellationPolicy.withdrawalFee'}
                            type="number"
                            pre={currency.symbol}
                            placeholder="E.g. 5"
                            validate={val => validateWithdrawalFee(val, values[eventEntryFeeField])}
                            className="hide-spinner"
                            themeV3
                                data-testid="event-edit.withdrawal-fee-input"
                            hoverText={(
                                <ul>
                                    <li>
                                        Withdrawal fee is optional and can be added only if player entry fees are enabled and must be less than the entry fee.
                                    </li>
                                    <li>
                                        Withdrawal fee will be charged only if a player is withdrawn from all registered event divisions.
                                    </li>
                                    <li>
                                        Withdrawal fee cannot be changed once the event has registrants.
                                    </li>
                                </ul>
                            )}
                            disabled={!values[eventEntryFeeField] || playersRegistered}
                        />
                        {!values[eventEntryFeeField] && (
                            <small className="mt8">Player entry fee must be added in order to add a withdrawal fee.</small>
                        )}
                    </div>
                    <div className="mt16">
                        <CheckBox
                            controlled={true}
                            id="chargeFee"
                            customLabel="Charge fee only after a certain date. If this is unselected then withdrawal fees will always be charged."
                            checked={showWithdrawFeeDate}
                            onChange={toggleWithdrawFeeDate}
                            customLabelClass="fs13"
                            disabled={!parseInt(values?.registrationCancellationPolicy?.withdrawalFee)}
                            data-testid="event-edit.charge-fee-date-checkbox"
                        />
                    </div>
                    {showWithdrawFeeDate && (
                        <div className="mt16">
                            <DateTimePicker
                                id="effectiveAfterDateTimeUtc"
                                field="registrationCancellationPolicy.effectiveAfterDateTimeUtc"
                                selected={effectiveDate}
                                minDate={regDateStart}
                                maxDate={tourDateEnd}
                                handleDateChange={handleEffectiveDateChange}
                                validate={showWithdrawFeeDate && required}
                                themeV3
                                    data-testid="event-edit.effective-after-date-time-utc-date-picker"
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

EntryFee.propTypes = {
    field: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    formApi: PropTypes.objectOf(any).isRequired,
    eventEntryFeeField: PropTypes.string.isRequired,
    currency: PropTypes.objectOf(any).isRequired,
    minimumEntryFee: PropTypes.number,
    formattedMinimumEntryFee: PropTypes.string,
    playersRegistered: PropTypes.bool,
};

EntryFee.defaultProps = {
    minimumEntryFee: defaultMinimumEntryFee,
    playersRegistered: false,
};

export default EntryFee;
